import type { FieldWidget } from '@cloudsign/shared';
import { t } from '../../lib/i18n';
import { NoticeView } from './noticeText';

export interface PreviewOption {
  id: string;
  label: string;
}

const BOX = 'block w-full rounded-lg border-2 border-slate-300 bg-white px-4 py-3 text-base text-slate-500';

/** A kiosk-like drawing of one question, used by the field dialog. Nothing in it can be filled in. */
export function FieldPreview({
  widget,
  label,
  helpText,
  required,
  config,
}: {
  widget: FieldWidget | null;
  label: string;
  helpText: string;
  required: boolean;
  config: Record<string, unknown>;
}) {
  const options = (Array.isArray(config['options']) ? config['options'] : []) as PreviewOption[];
  const unit = typeof config['unit'] === 'string' ? config['unit'] : '';
  const body = (() => {
    switch (widget) {
      case null:
        return <p className="text-sm text-slate-600">{t('fnUi.preview.empty')}</p>;
      case 'textarea':
        return <div aria-hidden="true" className={`${BOX} h-24`} />;
      case 'select':
        return (
          <div aria-hidden="true" className={BOX}>
            {t('fnUi.preview.choose')}
          </div>
        );
      case 'multi_select':
        return (
          <ul className="space-y-2">
            {options
              .filter((o) => o.label)
              .map((o) => (
                <li key={o.id} className="flex items-center gap-3 rounded-lg border-2 border-slate-300 px-4 py-3 text-base">
                  <span aria-hidden="true" className="h-5 w-5 rounded border-2 border-slate-400" />
                  {o.label}
                </li>
              ))}
          </ul>
        );
      case 'yes_no':
        return (
          <div className="flex gap-3">
            <span className="flex-1 rounded-lg border-2 border-slate-300 px-4 py-3 text-center text-base">{t('fnUi.preview.yes')}</span>
            <span className="flex-1 rounded-lg border-2 border-slate-300 px-4 py-3 text-center text-base">{t('fnUi.preview.no')}</span>
          </div>
        );
      case 'signature':
        return (
          <div aria-hidden="true" className="flex h-28 items-end rounded-lg border-2 border-dashed border-slate-300 p-3 text-sm text-slate-500">
            {t('fnUi.preview.signHere')}
          </div>
        );
      case 'acknowledge':
        return (
          <div className="space-y-3">
            {typeof config['message'] === 'string' && config['message'] ? (
              <div className="rounded-lg border-2 border-slate-200 bg-slate-50 p-4">
                <NoticeView body={config['message']} />
              </div>
            ) : null}
            <div className="flex items-center gap-3 text-base">
              <span aria-hidden="true" className="h-6 w-6 rounded border-2 border-slate-400" />
              {t('fnUi.preview.iAcknowledge')}
            </div>
          </div>
        );
      case 'member_picker':
        return (
          <div aria-hidden="true" className={BOX}>
            {t('fnUi.preview.pickMember')}
          </div>
        );
      default:
        return (
          <div aria-hidden="true" className={`${BOX} flex items-center justify-between`}>
            <span>&nbsp;</span>
            {widget === 'number' && unit ? <span>{unit}</span> : null}
          </div>
        );
    }
  })();
  return (
    <div data-testid="field-preview" className="rounded-xl border border-slate-200 bg-slate-100 p-4">
      <div className="rounded-xl bg-white p-5 shadow-sm">
        <p className="mb-1 text-lg font-semibold text-slate-900">
          {label || t('fnUi.preview.empty')}
          {required && label ? <span className="ml-2 text-sm font-normal text-slate-600">{t('fnUi.preview.requiredMark')}</span> : null}
        </p>
        {helpText ? <p className="mb-3 text-sm text-slate-600">{helpText}</p> : <div className="mb-3" />}
        {body}
      </div>
    </div>
  );
}
